import { type ReactNode, useEffect, useMemo } from 'react';

import { AffineIconRenderer } from './renderer/affine-icon';
import { type IconData, IconType } from './type';

const BlobIconRenderer = ({ blob, url }: { blob?: Blob; url?: string }) => {
  const src = useMemo(() => {
    if (url) return url;
    if (blob) return URL.createObjectURL(blob);
    return null;
  }, [blob, url]);

  useEffect(() => {
    // Only revoke object URLs we created from blobs
    if (!url && blob && src) {
      return () => URL.revokeObjectURL(src);
    }
    return;
  }, [url, blob, src]);

  if (!src) return null;

  return (
    <img
      src={src}
      width="1em"
      height="1em"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
      alt=""
    />
  );
};

export const IconRenderer = ({
  data,
  fallback,
}: {
  data?: IconData;
  fallback?: ReactNode;
}) => {
  if (!data) {
    return fallback ?? null;
  }

  if (data.type === IconType.Emoji && data.unicode) {
    return data.unicode;
  }
  if (data.type === IconType.AffineIcon && data.name) {
    return <AffineIconRenderer name={data.name} color={data.color} />;
  }
  if (data.type === IconType.Blob) {
    return <BlobIconRenderer blob={data.blob} url={data.url} />;
  }

  return fallback ?? null;
};
